import {
  ActionIcon,
  Button,
  Center,
  Checkbox,
  Group,
  Input,
  Menu,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { closeAllModals, openModal } from '@mantine/modals';
import { IconBinaryTree2, IconDownload, IconPencil, IconPhoto, IconTrash, IconTrashX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  FAQsItem,
  FaqSourceType,
  useBot,
  useFAQsList,
  useFAQsMethods,
  useFAQsPropertyAll,
  useDataSourcesModal,
} from '@mygpt/app';
import { SvgAdd, SvgSearch, SvgTrash } from '@/components/Common/icon';
import { SvgCalenderGray } from '@/components/Dashboard/icon';
import Th from '@/components/Datasets/DataSources/th';
import DateSelector from '@/components/DateSelector';
import { FaqsImport } from '@/components/Faqs/import';
import { FaqsItemEdit, FaqsItemEditProps } from '@/components/Faqs/item-edit';
import { FaqsItemRemove } from '@/components/Faqs/item-remove';
import { ExportDialog } from '@/components/Faqs/ExportDialog';
import TableLoading from '@/components/TableLoading';
import { useDatasetStore } from '@/store/dataset';

import classes from './list.module.css';

interface FaqsItemProps {
  item: FAQsItem;
  selected?: boolean;
  score?: number;
  onSelectedChange?: (selected: boolean) => void;
  onDelete?: (faqs: FAQsItem) => void;
  onEdit?: (faqs: FAQsItem) => void;
}

const FaqsItem: FC<FaqsItemProps> = ({ item, onDelete, onEdit, onSelectedChange, score, selected }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { botId } = useBot();
  const { datasetId } = useParams();

  const isTreeNode = item.hierarchy_parent_id !== null;

  // 将 Markdown 格式转换为纯文本用于列表显示
  const markdownToPlainText = (markdown: string): string => {
    return (
      markdown
        // 清理 tiptap 空段落占位符
        .replace(/&nbsp;/g, '')
        // 移除 Markdown 硬换行（反斜杠 + 换行）
        .replace(/\\\n/g, ' ')
        // 将换行符转换为空格
        .replace(/\n/g, ' ')
        // 移除多余的空格
        .replace(/\s+/g, ' ')
        .trim()
    );
  };

  const faqType = (key: string): string => {
    if (isTreeNode) {
      return t('embeddings.table.filter_by_type.qa_tree_node');
    }

    switch (key) {
      case 'user_input': {
        return t('embeddings.table.filter_by_type.user_input');
      }
      case 'user_import': {
        return t('embeddings.table.filter_by_type.user_import');
      }
      case 'conversation': {
        return t('embeddings.table.filter_by_type.conversation');
      }
      // ナレッジループ由来（サポートチケット取込 / 運営画面からの提案 / 台帳の投影）：業務名で見せ、台帳へ飛べる
      case 'zendesk_import': {
        return t('embeddings.table.filter_by_type.zendesk_import');
      }
      case 'zendesk_reply': {
        return t('embeddings.table.filter_by_type.zendesk_reply');
      }
      case 'knowledge_loop': {
        return t('embeddings.table.filter_by_type.knowledge_loop');
      }

      default:
        return key;
    }
  };

  const ledgerBacked = ['zendesk_import', 'zendesk_reply', 'knowledge_loop'].includes(item.sources || '');
  const openLedger = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!botId || !datasetId) return;
    navigate(`/bot/${botId}/datasets/${datasetId}/data-source/zendesk?tab=ledger&faq=${item.id}`);
  };

  return (
    <Table.Tr>
      <Table.Td w={300}>
        <Group wrap="nowrap" gap="xs" w={'full'} className="min-w-[200px]">
          <Checkbox disabled={isTreeNode} checked={selected} onChange={() => onSelectedChange?.(!selected)} />
          <div className="flex items-center gap-2">
            <Tooltip
              label={item.question}
              position="top-start"
              multiline
              styles={{ tooltip: { lineBreak: 'anywhere' } }}
            >
              <Text ta="left" lineClamp={1}>
                {item.question}
              </Text>
            </Tooltip>
            {item.has_image && (
              <span className="flex-1">
                <IconPhoto color="gray" />
              </span>
            )}
          </div>
        </Group>
      </Table.Td>
      <Table.Td w={400}>
        <div className="flex items-center gap-2 w-[400px]">
          <Tooltip
            label={markdownToPlainText(item.answer)}
            multiline
            styles={{ tooltip: { lineBreak: 'anywhere' } }}
            className="max-w-[500px]  break-all"
          >
            <Text ta="left" lineClamp={1}>
              {markdownToPlainText(item.answer)}
            </Text>
          </Tooltip>
        </div>
      </Table.Td>
      {/* <td>{item.rating}</td> */}
      {score !== undefined && (
        <Table.Td>
          <Group gap="xs" wrap="nowrap">
            <span className="inline-block px-2 py-1 rounded-full bg-gray-200 text-gray-900 text-xs text-ellipsis overflow-hidden max-w-[120px]">
              {Math.floor(score * 10000) / 100}%
            </span>
          </Group>
        </Table.Td>
      )}
      <Table.Td>
        {!item.answer_token_count && dayjs(item.updated_at).isBefore('2024-01-19') ? '-' : item.answer_token_count}
      </Table.Td>
      <Table.Td>
        {(item.sources || isTreeNode) && (
          <Tooltip
            label={ledgerBacked && !isTreeNode ? t('embeddings.table.open_ledger') : faqType(item.sources)}
            className="max-w-[500px]  break-all"
          >
            <span
              className={`inline-block px-3 py-1 rounded-full text-[#000] text-xs text-ellipsis overflow-hidden whitespace-nowrap max-w-[120px] ${
                ledgerBacked && !isTreeNode ? 'bg-teal-300 cursor-pointer underline decoration-dotted' : 'bg-violet-400'
              }`}
              onClick={ledgerBacked && !isTreeNode ? openLedger : undefined}
            >
              {faqType(item.sources)}
            </span>
          </Tooltip>
        )}
      </Table.Td>
      <Table.Td className="hidden sm:block">
        <span className="text-[13px] text-gray-400 text-nowrap">
          {dayjs(item.updated_at).format('MM/DD/YYYY HH:mm')}
        </span>
      </Table.Td>
      <Table.Td>
        <Center>
          {isTreeNode ? (
            <Tooltip label={t('faqs.tree.edit_node')} className="max-w-[500px]  break-all">
              <ActionIcon
                variant="subtle"
                loaderProps={{ size: 16 }}
                onClick={() => {
                  navigate(pathname, {
                    state: {
                      nodeId: item.id,
                      hierarchyParentId: item.hierarchy_parent_id,
                      hierarchyLevel: item.hierarchy_level,
                    },
                  });
                }}
              >
                <IconBinaryTree2 size={16} />
              </ActionIcon>
            </Tooltip>
          ) : (
            <>
              <Tooltip label={t('edit')}>
                <ActionIcon
                  variant="subtle"
                  onClick={() => {
                    onEdit?.(item);
                  }}
                  loaderProps={{ size: 16 }}
                >
                  <IconPencil size={16} />
                </ActionIcon>
              </Tooltip>

              <Tooltip label={t('delete_btn')}>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => {
                    onDelete?.(item);
                  }}
                  loaderProps={{ size: 16 }}
                >
                  <IconTrashX color="red" size={16} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
        </Center>
      </Table.Td>
    </Table.Tr>
  );
};

export const FaqsList = () => {
  const { t, i18n } = useTranslation();
  const matches = useMediaQuery('(max-height: 600px)');

  const { datasetId } = useParams();
  const {
    data: faqs,
    faqTypeValue,
    handleDateChange,
    loadList,
    loading,
    page,
    reverseSortDirection,
    searchList,
    setFaqTypeValue,
    setPage,
    setSorting,
    sortBy,
  } = useFAQsList(datasetId, { defaultPageSize: 50 });
  const { loading: loadingProperty } = useFAQsPropertyAll(datasetId, true);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [exportDialogOpened, setExportDialogOpened] = useState(false);
  const { addFAQs, batchDeleteFAQs, updateFAQs } = useFAQsMethods(datasetId);
  const isLoadedRef = useRef(false);

  const { isCloning, setCloningTipOpened, setCount } = useDatasetStore();
  const { setCounter, counter } = useDataSourcesModal();

  const allSelected = useMemo(() => {
    const disabledLen = faqs.filter((item) => item.hierarchy_parent_id !== null).length;
    return selectedIds.length > 0 && faqs?.length - disabledLen === selectedIds.length;
  }, [faqs, selectedIds]);

  const toggleAllChecked = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? faqs.filter((item) => item.hierarchy_parent_id === null).map((item) => item.id) : []);
    },
    [faqs]
  );

  const selectFAQs = useCallback((selectFAQs: FAQsItem) => {
    setSelectedIds((ids) => {
      if (ids.includes(selectFAQs.id)) {
        return ids.filter((id) => id !== selectFAQs.id);
      }
      return [...ids, selectFAQs.id];
    });
  }, []);

  useLayoutEffect(() => {
    if (debouncedSearch) {
      searchList({
        question: debouncedSearch,
        language: i18n.language,
      });
    } else if (isLoadedRef.current && datasetId) {
      loadList({ page: 1, language: i18n.language });
    }
  }, [datasetId, debouncedSearch, loadList, searchList, i18n.language]);

  useEffect(() => {
    isLoadedRef.current = true;
  }, []);

  useEffect(() => {
    setSelectedIds([]);
  }, [debouncedSearch, datasetId, page.page, page.size]);

  useEffect(() => {
    setCount('faq', page.total);
  }, [page.total]);

  const handleEdit = (item?: FAQsItem) => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    const { citation } = item || {};
    const isEdit = !!item;
    const onSubmit = async (params: Parameters<NonNullable<FaqsItemEditProps['onSubmit']>>[0]) => {
      if (isEdit) {
        await updateFAQs({ datasetId, faqsId: item.id, ...params });
      } else {
        await addFAQs({ datasetId, ...params });
        // 新增FAQ时触发数据源页面刷新文件类型计数
        setCounter(counter + 1);
      }
      closeAllModals();
    };
    openModal({
      title: item ? t('faqs.edit_title') : t('faqs.add_title'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 'lg',
      children: (
        <FaqsItemEdit
          datasetId={datasetId}
          faqId={item ? item.id : null}
          faqs={item}
          citation={citation || []}
          onSubmit={onSubmit}
          onSubmitText={item ? t('save') : t('faqs.action.add')}
        />
      ),
    });
  };

  const handleDelete = (...faqsIds: string[]) => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    const onDelete = async (params: Parameters<typeof batchDeleteFAQs>[0]) => {
      await batchDeleteFAQs(params);
      closeAllModals();
      // 删除FAQ时触发数据源页面刷新文件类型计数
      setCounter(counter + 1);
      const updatedSelectedIds = selectedIds.filter((item) => !params.faqsIds.includes(item));
      setSelectedIds(updatedSelectedIds);
      if (allSelected && page.page !== 1) {
        setPage({ page: page.page - 1 });
      } else {
        // 如果有搜索条件，重新执行搜索；否则重新加载列表
        if (debouncedSearch) {
          searchList({
            question: debouncedSearch,
            language: i18n.language,
          });
        } else {
          loadList({ page: page.page, language: i18n.language });
        }
      }
    };
    openModal({
      title: t('faqs.delete.title'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      children: <FaqsItemRemove datasetId={datasetId} faqsIds={faqsIds} onDelete={onDelete} />,
    });
  };

  const handleImport = () => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    openModal({
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: '60%',
      children: (
        <div className="pb-5 text-violet-500">
          <FaqsImport mode="modal" />
        </div>
      ),
    });
  };

  const renderContent = () => {
    if (loading || loadingProperty) {
      return <TableLoading />;
    }
    if (!faqs.length && !debouncedSearch) {
      return (
        <section className="rounded-[20px] bg-white border border-[#F8F6FF]">
          <section className="h-[400px] flex items-center justify-center">
            <Stack align={'center'}>
              <Title order={4} c="gray.5" w={matches ? '80%' : '100%'}>
                {t('embeddings.list.empty_tip')}
              </Title>
            </Stack>
          </section>
        </section>
      );
    }
    return (
      <>
        <div className="w-full overflow-scroll">
          <Table highlightOnHover className={classes.table} captionSide="bottom">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Group gap="xs">
                    <Checkbox checked={allSelected} onChange={() => toggleAllChecked(!allSelected)} />
                    {t('faqs.field.question')}
                  </Group>
                </Table.Th>
                <Table.Th>
                  <Group gap="xs">{t('faqs.field.answer')}</Group>
                </Table.Th>
                {/* <th>{t('faqs.field.rating')}</th> */}
                {debouncedSearch && <Table.Th>{t('faqs.field.match_score')}</Table.Th>}
                <Th
                  sorted={sortBy === 'answer_token_count'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('answer_token_count')}
                >
                  {t('embeddings.table.tokens')}
                </Th>
                <Table.Th>{t('embeddings.table.type')}</Table.Th>
                <Th
                  className="hidden sm:block"
                  sorted={sortBy === 'updated_at'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('updated_at')}
                >
                  {t('faqs.field.last_change')}
                </Th>
                <Table.Th>{t('faqs.field.action')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {faqs.map((item, index) => (
                <FaqsItem
                  item={item}
                  score={debouncedSearch && 'score' in item ? (item.score as number) : undefined}
                  key={index}
                  selected={selectedIds.includes(item.id)}
                  onSelectedChange={() => selectFAQs(item)}
                  onEdit={() => handleEdit(item)}
                  onDelete={(item) => handleDelete(item.id)}
                />
              ))}
            </Table.Tbody>
          </Table>
        </div>
        {!debouncedSearch && page.pages > 1 && (
          <Center className="pb-4 bg-white mt-0 pt-3 w-full">
            <Pagination
              value={page.page}
              onChange={(value) => setPage({ page: value })}
              total={page.pages}
              color="violet.5"
            />
          </Center>
        )}
      </>
    );
  };

  return (
    <div>
      <div className="flex flex-col xl:flex-row xl:justify-between flex-wrap mb-5 gap-3">
        <div className="flex flex-1 gap-3 items-center xl:justify-start mt-2 xl:mt-0">
          <Input
            className="grow xl:grow-0 xl:min-w-[30%]"
            leftSection={<SvgSearch />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder={t('faqs.action.search_placeholder')}
          />

          <Select
            size="sm"
            style={{ width: 150 }}
            value={faqTypeValue}
            onChange={(value: string | null) => {
              setFaqTypeValue(value as FaqSourceType);
            }}
            data={[
              { value: '', label: t('embeddings.table.filter_by_type.all') },
              { value: 'user_input', label: t('embeddings.table.filter_by_type.user_input') },
              { value: 'user_import', label: t('embeddings.table.filter_by_type.user_import') },
              { value: 'conversation', label: t('embeddings.table.filter_by_type.conversation') },
            ]}
            allowDeselect={false}
          />

          <DateSelector
            hasAll
            showCustomDateTime
            onDateChange={handleDateChange}
            radius={8}
            p={'7px 15px'}
            leftSection={<SvgCalenderGray />}
            styles={() => ({
              root: {
                color: '#000',
                border: '1px solid #E4E7EC',
                backgroundColor: '#FFF',

                '&: hover': {
                  borderColor: '#5A4CB7',
                },
              },
              leftIcon: {
                color: 'gray',
              },
            })}
          />
        </div>

        <Menu shadow="md">
          <Menu.Target>
            <Button leftSection={<SvgAdd />}>{t('faqs.action.add')}</Button>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              leftSection={<SvgAdd />}
              onClick={(e) => {
                e.stopPropagation();
                handleImport();
              }}
            >
              {t('faqs.action.import')}
            </Menu.Item>
            <Menu.Item
              leftSection={<SvgAdd />}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit();
              }}
            >
              {t('faqs.action.add_question')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>

        <Button
          leftSection={<IconDownload size={16} className="opacity-80" />}
          onClick={() => setExportDialogOpened(true)}
        >
          {t('faqs.action.export')}
        </Button>

        <Button
          color="red"
          leftSection={<SvgTrash />}
          variant="outline"
          className="filled"
          disabled={selectedIds.length === 0}
          onClick={() => handleDelete(...selectedIds)}
        >
          {t('faqs.action.delete')}
        </Button>
      </div>
      {renderContent()}

      <ExportDialog
        opened={exportDialogOpened}
        onClose={() => setExportDialogOpened(false)}
        datasetId={datasetId}
        currentPage={page.page}
        pageSize={page.size}
        orderBy={`${reverseSortDirection ? '-' : ''}${sortBy}`}
        selectedIds={selectedIds}
      />
    </div>
  );
};
